<template>
  <div class="trestle-root">
    <module-navigation-bar title="Trestle checker">
      <template #module-nav-logo>
        <img
          v-if="isDaiwaLicense"
          alt=""
          height="40px"
          src="https://www.daiwahousemodular.eu/wp-content/uploads/daiwa_logo.svg"
        />
      </template>
    </module-navigation-bar>

    <template v-if="dhmeTrestleCheckerStatus === 'success'">
      <!-- Top toolbar -->
      <div class="trestle-toolbar ant-glass-background ant-border-bottom">
        <div class="toolbar-select">
          <v-combobox
            v-model="selectedAssemblyHall"
            :items="dhmeTrestleCheckerHalls"
            clearable
            dense
            filled
            hide-details
            item-text="hall"
            placeholder="Hall"
            return-object
            single-line
            @click:clear="resetState"
          />
        </div>

        <div class="toolbar-select">
          <v-combobox
            v-model="selectedProject"
            :disabled="!selectedAssemblyHall"
            :items="assemblyHallProjects"
            clearable
            dense
            filled
            hide-details
            item-text="name"
            placeholder="Project"
            return-object
            single-line
          />
        </div>

        <template v-if="selectedProject && options.length">
          <v-divider vertical class="toolbar-divider mx-3" />
          <div class="toolbar-toggles">
            <v-btn-toggle
              v-model="selectedOptionIds"
              multiple
              mandatory
              color="primary"
              dense
            >
              <v-btn
                v-for="opt in options"
                :key="opt.id"
                :value="opt.id"
                small
              >
                {{ opt.title }}
              </v-btn>
            </v-btn-toggle>
          </div>
          <v-divider vertical class="toolbar-divider mx-3" />
          <div class="toolbar-select">
            <v-autocomplete
              v-model="moduleFilter"
              :items="allModuleIds"
              clearable
              dense
              filled
              hide-details
              placeholder="Module ID"
              single-line
            />
          </div>
        </template>
      </div>

      <!--
        Main row: always fits in the viewport.
        Left side = scroll container (overflow-x: auto, flex: 1).
        Right side = detail pane (fixed width, only when a trestle is selected).

        The trick: the scroll container is a plain block (not also a flex container).
        Inside it lives the actual flex row of columns that can grow freely.
      -->
      <div class="trestle-main-row">
        <!-- Scroll container -->
        <div class="trestle-scroll-container">
          <!-- Inner flex row — can be wider than its parent -->
          <div class="trestle-inner-row">
            <template v-if="selectedProject && activeOptions.length">
              <div
                v-for="opt in activeOptions"
                :key="opt.id"
                class="trestle-column ant-border-right"
              >
                <!-- Column header -->
                <div class="trestle-column-header ant-border-bottom px-3 py-2 d-flex align-center font-weight-medium">
                  <span>{{ opt.title }}</span>
                  <v-spacer />
                  <v-progress-circular
                    v-if="trestlesLoading[opt.id]"
                    color="primary"
                    indeterminate
                    size="18"
                    width="2"
                  />
                </div>

                <!-- Loading (first fetch) -->
                <div
                  v-if="trestlesLoading[opt.id] && !(trestlesByOption[opt.id] || []).length"
                  class="trestle-column-body d-flex align-center justify-center"
                >
                  <v-progress-circular color="primary" indeterminate />
                </div>

                <!-- Trestle list -->
                <div
                  v-else-if="(trestlesByOption[opt.id] || []).length"
                  class="trestle-column-body pa-3"
                >
                  <div
                    v-for="(trestle, index) in trestlesByOption[opt.id]"
                    :key="index"
                    class="trestle-list-item d-flex align-center ant-border-bottom pb-2 mb-2 px-2"
                    :class="{ 'is-selected': selectedTrestle === trestle }"
                    @click="selectedTrestle = trestle"
                  >
                    <span class="trestle-index mr-2">{{ index + 1 }}</span>
                    <div style="width: 24px; text-align: center">
                      <v-icon v-if="isTrestleProduced(trestle)" color="success" small>
                        mdi-check-circle
                      </v-icon>
                    </div>
                    <div :class="{ 'trestle-selected-ring': selectedTrestle === trestle }">
                      <component
                        :is="isDouble ? 'BaseDhmeDoubleTrestle' : 'BaseDhmeSingleTrestle'"
                        :selected-module="moduleFilter"
                        :trestle="trestle"
                        :type-color-dictionary="typeColorDictionary"
                      />
                    </div>
                  </div>
                </div>

                <!-- Empty -->
                <div v-else class="trestle-column-body d-flex align-center justify-center">
                  <span class="text--secondary">No trestles found.</span>
                </div>
              </div>
            </template>

            <!-- No options selected yet but project is chosen -->
            <div
              v-else-if="selectedProject"
              class="trestle-column d-flex align-center justify-center"
            >
              <v-progress-circular color="primary" indeterminate />
            </div>
          </div>
        </div>

        <!-- Detail pane — fixed width, always on screen -->
        <div
          v-if="selectedTrestle"
          class="trestle-detail-pane ant-glass-background ant-border-left"
        >
          <div class="trestle-detail-header ant-border-bottom px-4 py-2 d-flex align-center">
            <span class="text-subtitle-2">Trestle detail</span>
            <v-spacer />
            <v-btn icon small @click="selectedTrestle = null">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="trestle-detail-body px-6 py-4">
            <component
              :is="isDouble ? 'BaseDhmeDoubleTrestle' : 'BaseDhmeSingleTrestle'"
              :is-large="true"
              :selected-module="highlightedModule"
              :selected-trestle="selectedTrestle"
              :trestle="selectedTrestle"
              :type-color-dictionary="typeColorDictionary"
              @clickedElement="selectElementToScan"
            />

            <v-subheader class="pa-0 mt-5 mb-2" style="height: auto">
              <div class="text-subtitle-2 py-1">Modules</div>
            </v-subheader>
            <div class="d-flex flex-column">
              <v-tooltip
                v-for="module in uniqueModules"
                :key="module"
                left
              >
                <template #activator="{ on }">
                  <v-chip
                    :color="getColorByModuleId(module)"
                    :style="{
                      opacity: moduleFilter
                        ? moduleFilter !== module ? 0.3 : 1
                        : highlightedModule && highlightedModule !== module ? 0.3 : 1,
                    }"
                    class="mb-1"
                    small
                    @click="toggleModuleFilter(module)"
                    @mouseenter="highlightedModule = module"
                    @mouseleave="highlightedModule = null"
                    v-on="on"
                  >
                    {{ module }}
                  </v-chip>
                </template>
                <span>{{ getTypeByModuleId(module) }}</span>
              </v-tooltip>
            </div>
          </div>

          <qr-scanner-dialog
            v-model="qrDialog"
            :title="`Scan ${scannedElement?.element_type}`"
            @decoded="validateElement"
          />
        </div>
      </div>
    </template>

    <div v-else class="d-flex flex-1 justify-center align-center">
      <ant-loading />
    </div>
  </div>
</template>

<script>
import ModuleNavigationBar from '@/components/Modules/ModuleNavigationBar.vue';
import { mapGetters } from 'vuex';
import { DHME_M_TRESTLE_CHECKER } from '@/modules/modules';
import AntLoading from '@/components/AntLoading.vue';
import { executeCustomModuleCall } from '@/services/api/module.api';
import ColorHelper from '@/services/color-helper';
import BaseDhmeSingleTrestle from '@/components/Modules/Daiwa-House-Modular-Europe/Base/BaseDhmeSingleTrestle.vue';
import BaseDhmeDoubleTrestle from '@/components/Modules/Daiwa-House-Modular-Europe/Base/BaseDhmeDoubleTrestle.vue';
import QrScannerDialog from '@/components/Base/QrScannerDialog.vue';

export default {
  name: 'DHMEMTrestleChecker',
  components: {
    ModuleNavigationBar,
    AntLoading,
    BaseDhmeSingleTrestle,
    BaseDhmeDoubleTrestle,
    QrScannerDialog,
  },
  data() {
    return {
      selectedAssemblyHall: null,
      selectedProject: null,
      assemblyHallProjects: [],
      assemblyLocations: [],
      options: [],
      selectedOptionIds: [],
      trestlesByOption: {},
      trestlesLoading: {},
      selectedTrestle: null,
      projectTrestles: [],
      typeColorDictionary: {},
      highlightedModule: null,
      moduleFilter: null,
      qrDialog: false,
      scannedElement: null,
    };
  },
  computed: {
    ...mapGetters([
      'project',
      'dhmeTrestleCheckerStatus',
      'dhmeTrestleCheckerHalls',
      'dhmeTrestleCheckerLanes',
      'isDaiwaLicense',
    ]),
    moduleId() {
      return this.project.modules.find(
        (m) => m.route === DHME_M_TRESTLE_CHECKER
      ).id;
    },
    isDouble() {
      return !this.selectedAssemblyHall?.automated;
    },
    activeOptions() {
      return this.options.filter((o) => this.selectedOptionIds.includes(o.id));
    },
    allModuleIds() {
      const ids = Object.values(this.trestlesByOption)
        .flat()
        .flatMap((t) => t.positions.flatMap((p) => p.elements))
        .map((e) => e.module_id)
        .filter(Boolean);
      return [...new Set(ids)].sort();
    },
    uniqueModules() {
      if (!this.selectedTrestle) return [];
      return [
        ...new Set(
          this.selectedTrestle.positions
            .flatMap((p) => p.elements)
            .map((e) => e.module_id)
        ),
      ];
    },
  },
  watch: {
    dhmeTrestleCheckerHalls: {
      immediate: true,
      handler() {
        this.resetState();
      },
    },
    selectedAssemblyHall(val) {
      this.resetState();
      if (val) {
        executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchHallProjects',
          { hall: val.id }
        )
          .then((projects) => {
            this.assemblyHallProjects = projects;
          })
          .catch(() => {
            this.$store.commit('showNotification', {
              content: 'Failed to load projects.',
              color: 'error',
            });
          });
      }
    },
    selectedProject: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.selectedTrestle = null;
          this.options = [];
          this.selectedOptionIds = [];
          return;
        }
        executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'fetchHallLocations',
          { hall: this.selectedAssemblyHall.id, project: val.id }
        )
          .then((locations) => {
            this.assemblyLocations = locations;
            if (this.isDouble) {
              const lanes = [
                ...new Set(locations.map((l) => l.assembly_lane)),
              ].sort((a, b) => a - b);
              this.options = lanes.map((l) => ({
                id: String(l),
                title:
                  this.dhmeTrestleCheckerLanes.find((x) => x.id === l)
                    ?.title ?? String(l),
                locations: locations
                  .filter((loc) => loc.assembly_lane === l)
                  .map((loc) => loc.id),
              }));
            } else {
              this.options = [
                { id: '1', title: 'Header north', position: ['1'] },
                { id: '2', title: 'Header south', position: ['2'] },
                { id: '3', title: 'Side west', position: ['3'] },
                { id: '4', title: 'Side east', position: ['4'] },
                { id: '5', title: 'Inside walls', position: ['5'] },
                { id: '6', title: 'Roofs', position: ['6'] },
                { id: '8', title: 'Exterior', position: ['8'] },
              ];
            }
            if (this.options.length) {
              this.selectedOptionIds = [this.options[0].id];
              this.loadTrestles(this.options[0]);
              this.getProjectTrestles();
            }
          })
          .catch(() => {
            this.$store.commit('showNotification', {
              content: 'Failed to load locations.',
              color: 'error',
            });
          });
      },
    },
    selectedOptionIds(newIds, oldIds) {
      const added = newIds.filter((id) => !oldIds.includes(id));
      added.forEach((id) => {
        const opt = this.options.find((o) => o.id === id);
        if (opt) this.loadTrestles(opt);
      });

      const removed = oldIds.filter((id) => !newIds.includes(id));
      if (removed.length) {
        this.selectedTrestle = null;
      }
    },
  },
  mounted() {
    this.$store.dispatch('fetchDhmeTrestleCheckerData', {
      moduleId: this.moduleId,
      sessionId: this.$route.params.sessionId ?? null,
    });
  },
  methods: {
    toggleModuleFilter(module) {
      this.moduleFilter = this.moduleFilter === module ? null : module;
    },

    resetState() {
      this.selectedProject = null;
      this.options = [];
      this.selectedOptionIds = [];
      this.trestlesByOption = {};
      this.trestlesLoading = {};
      this.selectedTrestle = null;
      this.projectTrestles = [];
      this.typeColorDictionary = {};
      this.highlightedModule = null;
      this.moduleFilter = null;
    },

    loadTrestles(opt) {
      this.$set(this.trestlesLoading, opt.id, true);
      this.loadTrestlesImmediate(opt);
    },

    async loadTrestlesImmediate(opt) {
      try {
        let trestles = [];
        if (this.isDouble) {
          trestles = await executeCustomModuleCall(
            this.project.id,
            this.moduleId,
            'fetchDoubleTrestles',
            { project: this.selectedProject.id, locations: opt.locations }
          );
        } else {
          trestles = await executeCustomModuleCall(
            this.project.id,
            this.moduleId,
            'fetchTrestles',
            { project: this.selectedProject.id, position: opt.position[0] }
          );
        }
        this.$set(this.trestlesByOption, opt.id, trestles);
        this.updateColorDictionary(trestles);
      } catch {
        this.$store.commit('showNotification', {
          content: 'Failed to load trestles.',
          color: 'error',
        });
      } finally {
        this.$set(this.trestlesLoading, opt.id, false);
      }
    },

    async getProjectTrestles() {
      this.projectTrestles = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'getProjectTrestles',
        { project_id: this.selectedProject.id }
      );
    },

    updateColorDictionary(trestles) {
      const all = trestles.flatMap((t) =>
        t.positions.flatMap((p) => p.elements)
      );
      const types = [...new Set(all.map((e) => e.module_type))];
      types.forEach((type) => {
        if (!this.typeColorDictionary[type]) {
          this.$set(
            this.typeColorDictionary,
            type,
            ColorHelper.generateLightColorHex()
          );
        }
      });
    },

    isTrestleProduced(t) {
      return this.projectTrestles.some(
        (pt) =>
          pt.first_element ===
          t.positions.flatMap((p) => p.elements).find((e) => e)?.element_id
      );
    },

    selectElementToScan(element) {
      if (!element.production_id) {
        this.scannedElement = element;
        this.highlightedModule = element.module_id;
        this.qrDialog = true;
      } else {
        this.$store.commit('showNotification', {
          content: 'Already scanned',
          color: 'warning',
        });
      }
    },

    async validateElement(decoded) {
      try {
        const response = await executeCustomModuleCall(
          this.project.id,
          this.moduleId,
          'scanElement',
          {
            project_id: this.selectedProject.id,
            element_id: this.scannedElement.element_id,
            scanned_url: decoded,
          }
        );
        this.selectedTrestle.positions.forEach((pos) =>
          pos.elements.forEach((el) => {
            if (el.element_id === this.scannedElement.element_id) {
              el.production_id = response;
            }
          })
        );
        await this.getProjectTrestles();
        this.qrDialog = false;
      } catch (e) {
        this.$store.commit('showNotification', {
          content: e.message,
          color: 'error',
        });
      }
    },

    getColorByModuleId(mid) {
      const el = this.selectedTrestle.positions
        .flatMap((p) => p.elements)
        .find((e) => e.module_id === mid);
      return el ? this.typeColorDictionary[el.module_type] : '';
    },

    getTypeByModuleId(mid) {
      const el = this.selectedTrestle.positions
        .flatMap((p) => p.elements)
        .find((e) => e.module_id === mid);
      return el ? el.module_type : '';
    },
  },
};
</script>

<style scoped lang="scss">
/* Root: column flex, fills the module container */
.trestle-root {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

/* Toolbar: fixed height, never wraps */
.trestle-toolbar {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 56px;
  padding: 0 16px;
  gap: 12px;
}

.toolbar-select {
  width: 190px;
  flex-shrink: 0;
}

.toolbar-divider {
  height: 28px;
  align-self: center;
}

/* The toggle section takes remaining toolbar space and scrolls
   if there are more buttons than will fit */
.toolbar-toggles {
  flex: 1 1 0%;
  min-width: 0;
  overflow-x: auto;
  /* hide scrollbar — users drag or the buttons simply fit */
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

/*
  Main row: fills all remaining height.
  overflow: hidden is critical — it stops the row from growing past the viewport
  and forces the scroll container to be the one that overflows.
*/
.trestle-main-row {
  display: flex;
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
}

/*
  Scroll container: plain block (NOT a flex container itself).
  flex: 1 1 0% gives it all remaining width after the detail pane.
  overflow-x: auto provides the horizontal scrollbar.
  overflow-y: hidden prevents a double scrollbar — each column scrolls its own body.
*/
.trestle-scroll-container {
  flex: 1 1 0%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
}

/*
  Inner row: this is the actual flex container of columns.
  It lives inside the scroll container and can be wider than it.
  height: 100% makes all columns fill the available height.
*/
.trestle-inner-row {
  display: flex;
  height: 100%;
}

/* Each column: fixed width, never shrinks, fills full height as a flex column */
.trestle-column {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.trestle-column-header {
  flex-shrink: 0;
}

/* Column body scrolls vertically independently */
.trestle-column-body {
  flex: 1 1 0%;
  overflow-y: auto;
}

/* Detail pane: fixed width, always on-screen to the right */
.trestle-detail-pane {
  flex: 0 0 460px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.trestle-detail-header {
  flex-shrink: 0;
}

.trestle-detail-body {
  flex: 1 1 0%;
  overflow-y: auto;
}

/* List items */
.trestle-index {
  font-size: 12px;
  color: #9e9e9e;
  min-width: 20px;
  text-align: right;
  flex-shrink: 0;
}

.trestle-list-item {
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 150ms;

  &:hover {
    background-color: #f5f5f5;
  }
}

.trestle-selected-ring {
  outline: 3px dashed #1976d2;
  border-radius: 2px;
}

::v-deep .v-dialog {
  position: absolute;
  left: 0;
  top: 0;
}
</style>
